// LiquidityTab.js — Liquidity Preservation pillar home
// Sub-tabs: Ladder, Rollover, Netting, Secondary Market, Repatriation

import React, { useState, useMemo } from 'react';
import {
  Calendar, RefreshCw, ArrowRightLeft, Building2, Globe,
  CheckCircle, Clock, AlertTriangle, ChevronRight
} from 'lucide-react';

import CashFlowsTab from '../portfolio/CashFlowsTab';
import usePortfolioData from '../../hooks/usePortfolioData';
import { ROLLOVER_INSTRUCTIONS, REPATRIATIONS } from '../../data/dealingMockData';
import { calcNetting } from '../../lib/dealing';

const SUB_TABS = [
  { key: 'ladder', label: 'Ladder', icon: Calendar },
  { key: 'rollover', label: 'Rollover', icon: RefreshCw },
  { key: 'netting', label: 'Netting', icon: ArrowRightLeft },
  { key: 'secondary', label: 'Secondary Market', icon: Building2 },
  { key: 'repatriation', label: 'Repatriation', icon: Globe },
];

const ROLLOVER_OPTIONS = [
  { value: 'auto_same_tenor', label: 'Auto-roll (same tenor)' },
  { value: 'auto_tenor:91', label: 'Auto-roll into 91-day' },
  { value: 'auto_tenor:182', label: 'Auto-roll into 182-day' },
  { value: 'auto_tenor:364', label: 'Auto-roll into 364-day' },
  { value: 'none', label: 'Do not roll — repatriate' },
];

const LS_KEY = 'ff_rollover_instructions';

const formatCurrency = (v) => {
  if (v == null) return '\u2014';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

const formatDate = (d) => {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusIcon = (completed) => {
  if (completed) return <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />;
  return <Clock className="w-3.5 h-3.5 text-zinc-300" />;
};

// ─── Component ──────────────────────────────────────────────────────────

const LiquidityTab = ({ role, onNavigateSecondary }) => {
  const [activeSubTab, setActiveSubTab] = useState('ladder');
  const { positions, cashFlows } = usePortfolioData();

  // Rollover instructions — persisted to localStorage
  const [rolloverEdits, setRolloverEdits] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });

  const handleRolloverChange = (positionId, value) => {
    setRolloverEdits(prev => {
      const next = { ...prev, [positionId]: value };
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Netting preview — next settlement date example
  const nettingPreview = useMemo(() => {
    // Mock: upcoming payment of $2M due, with two same-day maturities
    const paymentDue = 2000000;
    const sameDayMaturities = [
      { id: 'POS-KE-091-001', amount: 980000, instrument: 'Kenya 91-day T-Bill' },
      { id: 'POS-KE-182-002', amount: 520000, instrument: 'Kenya 182-day T-Bill' },
    ];
    return { ...calcNetting(paymentDue, sameDayMaturities), sameDayMaturities, settlementDate: '2026-11-01' };
  }, []);

  // Filter positions by role for the ladder
  const filteredPositions = useMemo(() => {
    if (role === 'investor_individual') {
      return positions.filter(p => p.entity === 'ENT-003');
    }
    return positions;
  }, [role, positions]);

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="bg-white border border-zinc-200 rounded-none p-1 flex flex-wrap gap-1">
        {SUB_TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeSubTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveSubTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-[10px] font-mono uppercase tracking-[0.12em] transition-all ${
                isActive
                  ? 'bg-black text-white'
                  : 'bg-white text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Ladder — CashFlowsTab */}
      {activeSubTab === 'ladder' && (
        <CashFlowsTab positions={filteredPositions} role={role} cashFlows={cashFlows} />
      )}

      {/* Rollover Instructions */}
      {activeSubTab === 'rollover' && (
        <div className="bg-white border border-zinc-200 rounded-none p-6">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Rollover Instructions</h3>
          <p className="text-xs text-zinc-400 mb-4">Set rollover preferences for maturing positions. Changes are saved locally until the backend endpoint is live.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Position</th>
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Tenor</th>
                  <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Face Amount</th>
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Maturity</th>
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Instruction</th>
                </tr>
              </thead>
              <tbody>
                {ROLLOVER_INSTRUCTIONS.map((ri) => (
                  <tr key={ri.positionId} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-2 px-3 font-mono text-xs font-bold">{ri.positionId}</td>
                    <td className="py-2 px-3 font-mono text-xs">{ri.tenor}-day</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{formatCurrency(ri.faceAmount)}</td>
                    <td className="py-2 px-3 text-xs text-zinc-500">{formatDate(ri.maturityDate)}</td>
                    <td className="py-2 px-3">
                      <select
                        value={rolloverEdits[ri.positionId] || ri.instruction}
                        onChange={(e) => handleRolloverChange(ri.positionId, e.target.value)}
                        className="font-mono text-xs border border-zinc-200 rounded-none px-2 py-1 focus:outline-none focus:border-zinc-400"
                      >
                        {ROLLOVER_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Netting Preview */}
      {activeSubTab === 'netting' && (
        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Netting Preview</h3>
            <p className="text-xs text-zinc-400 mb-4">
              Next settlement: <span className="font-bold text-zinc-700">{formatDate(nettingPreview.settlementDate)}</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="border border-zinc-100 p-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400 block mb-1">Payment Due</span>
                <p className="font-mono text-lg font-bold text-black">{formatCurrency(nettingPreview.paymentDue)}</p>
              </div>
              <div className="border border-zinc-100 p-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400 block mb-1">Maturities (same day)</span>
                <p className="font-mono text-lg font-bold text-black">{formatCurrency(nettingPreview.totalMaturities)}</p>
              </div>
              <div className="border border-zinc-100 p-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400 block mb-1">Net Payable</span>
                <p className="font-mono text-lg font-bold text-emerald-700">{formatCurrency(nettingPreview.netPayable)}</p>
                {nettingPreview.nettingApplied && (
                  <span className="font-mono text-[9px] text-emerald-600">netting applied</span>
                )}
              </div>
            </div>

            {/* Same-day maturities detail */}
            <h4 className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400 mb-2">Offsetting Maturities</h4>
            <div className="space-y-1">
              {nettingPreview.sameDayMaturities.map(m => (
                <div key={m.id} className="flex items-center justify-between py-1.5 border-b border-zinc-100">
                  <span className="font-mono text-xs">{m.instrument}</span>
                  <span className="font-mono text-xs text-zinc-600">{formatCurrency(m.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Secondary Market */}
      {activeSubTab === 'secondary' && (
        <div className="bg-white border border-zinc-200 rounded-none p-8 text-center">
          <Building2 className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-zinc-800 mb-2">Secondary Market</h3>
          <p className="text-sm text-zinc-500 max-w-md mx-auto mb-4">
            List instruments for sale, browse active listings, and manage bids.
          </p>
          {onNavigateSecondary ? (
            <button
              onClick={onNavigateSecondary}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-700 text-white font-mono text-xs uppercase tracking-wider rounded-none hover:bg-zinc-800 transition-colors"
            >
              Open Secondary Market
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <a
              href="/investor/admin/liquidity"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-700 text-white font-mono text-xs uppercase tracking-wider rounded-none hover:bg-zinc-800 transition-colors"
            >
              View Secondary Market
              <ChevronRight className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      )}

      {/* Repatriation Pipeline */}
      {activeSubTab === 'repatriation' && (
        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Repatriation Pipeline</h3>
            <p className="text-xs text-zinc-400 mb-6">Track your matured proceeds from local settlement through foreign exchange and wire to home account.</p>
          </div>

          {REPATRIATIONS.map((rep) => (
            <div key={rep.id} className="bg-white border border-zinc-200 rounded-none p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="font-mono text-xs font-bold">{rep.id}</span>
                  <span className="ml-2 font-mono text-[10px] text-zinc-400">{rep.market} &middot; {rep.currency} {rep.faceAmount.toLocaleString()}</span>
                </div>
                <div className="text-right">
                  <span className="font-mono text-[10px] text-zinc-400">Maturity: {formatDate(rep.maturityDate)}</span>
                  {rep.daysToCashHome && (
                    <span className="ml-2 font-mono text-[10px] text-zinc-500">~{rep.daysToCashHome}d to cash home</span>
                  )}
                </div>
              </div>

              {/* Stage pipeline */}
              <div className="flex items-center gap-1 overflow-x-auto pb-2">
                {rep.stages.map((stage, i) => {
                  const isCurrent = stage.key === rep.stage;
                  const isComplete = !!stage.completedAt;
                  return (
                    <React.Fragment key={stage.key}>
                      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 border rounded-none text-[9px] font-mono uppercase whitespace-nowrap ${
                        isComplete
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : isCurrent
                            ? 'bg-zinc-700 border-zinc-700 text-white'
                            : 'bg-white border-zinc-200 text-zinc-400'
                      }`}>
                        {statusIcon(isComplete)}
                        {stage.label}
                      </div>
                      {i < rep.stages.length - 1 && (
                        <div className={`w-4 h-px ${isComplete ? 'bg-emerald-300' : 'bg-zinc-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Blockers */}
              {rep.blockers.length > 0 && (
                <div className="mt-3 space-y-1">
                  {rep.blockers.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-none">
                      <AlertTriangle className="w-3 h-3" />
                      {b.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Financial summary */}
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {rep.whtAmount != null && (
                  <div>
                    <span className="font-mono text-[9px] uppercase text-zinc-400 block">WHT</span>
                    <span className="font-mono text-xs">{rep.currency} {rep.whtAmount.toLocaleString()}</span>
                  </div>
                )}
                {rep.netProceeds != null && (
                  <div>
                    <span className="font-mono text-[9px] uppercase text-zinc-400 block">Net Proceeds</span>
                    <span className="font-mono text-xs">{rep.currency} {rep.netProceeds.toLocaleString()}</span>
                  </div>
                )}
                {rep.fxOutRate != null && (
                  <div>
                    <span className="font-mono text-[9px] uppercase text-zinc-400 block">FX Out Rate</span>
                    <span className="font-mono text-xs">{rep.fxOutRate}</span>
                  </div>
                )}
                {rep.expectedUsd != null && (
                  <div>
                    <span className="font-mono text-[9px] uppercase text-zinc-400 block">Expected USD</span>
                    <span className="font-mono text-xs">{formatCurrency(rep.expectedUsd)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiquidityTab;
